import SyncDisabledIcon from '@mui/icons-material/SyncDisabled';
import { Box, Slider, Stack, Typography } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { ButtonComponent, colors, TextFieldComponent } from '@watter/shared-components';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import watterImage from '../../assets/images/heater-setting-image.png';
import { RemoveHeaterPopup } from '../../components/removeHeaterPopUp/remove-heater-popup';
import { StaticData } from '../../config/enum/staticData';
import { GET_MACHINE_CONFIG, UPDATE_MACHINE_CONFIG } from '../../graphql/heater-details';
import { RemoveDeviceMutation } from '../../graphql/mutation/removeDevice';
import { client } from '../../service/apolloClient';
import { RootState } from '../../store';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { setIsMetricsAPICalled, setMetricsData } from '../../store/reducers/heaterSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { formatDateWithTimeWithSeconds } from '../../utils/helper';
import { NavigationConfig } from '../../utils/navigation-constants';
import { MachineConfigData } from '../../web-socket/socket.types';
import { useSocket } from '../../web-socket/useSocket';
import { WSInstance } from '../../web-socket/web-socket-service';
import { HeaterDetailWrapper } from './heater-details-styles';

const CustomValueLabel = ({
    value,
    marks,
    isValue,
}: {
    value: number | string;
    marks: { value: number }[];
    isValue: boolean;
}) => {
    let leftOffset = -20;
    if (value === marks[0].value) leftOffset = -10;
    else if (value === marks[1].value) leftOffset = -35;

    if (!isValue) {
        return (
            <Typography
                variant="h5"
                sx={{
                    fontSize: '14px',
                    position: 'absolute',
                    bottom: 5,
                    left: leftOffset,
                }}
            >
                --
            </Typography>
        );
    }
    return (
        <Typography
            variant="h5"
            sx={{
                fontSize: '14px',
                position: 'absolute',
                bottom: 5,
                left: leftOffset,
            }}
        >
            {value}°F
        </Typography>
    );
};
const HeaterDetails = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const userId = useSelector((state: any) => state?.user?.userId);
    const token = useSelector((state: any) => state.app.session?.token);
    const [heaterName, setHeaterName] = useState('');
    const [heaterTemperature, setHeaterTemperature] = useState(0);
    const [showEditButton, setShowEditButton] = useState(true);
    const [showApplyButton, setShowApplyButton] = useState(false);
    const [errorHeaterName, setErrorHeaterName] = useState(false);
    const [isShowRemoveHeaterPopup, setShowRemoveHeaterPopup] = useState(false);

    const [isHeaterNameConfigPending, setIsHeaterNameConfigPending] = useState(false);
    const [isHeaterTemperatureConfigPending, setIsHeaterTemperatureConfigPending] = useState(false);
    const [heaterNameUpdatedAt, setHeaterNameUpdatedAt] = useState<any>(null);
    const [heaterTemperatureUpdatedAt, setHeaterTemperatureUpdatedAt] = useState<any>(null);
    const staticDataPayload = useSelector((state: RootState) => state?.staticDataState);
    const deviceId = useSelector((state: any) => state.heater.metricsData?.machine_id);
    const [heaterDetails, setHeaterConfigInfo] = useState<any>({});
    // Map staticListingsData temperature to slider marks
    const tempStaticData = staticDataPayload?.[StaticData.TEMPERATURE];
    const marks = [
        {
            value: tempStaticData?.minValue,
            label: `${tempStaticData?.minValue}°F`,
        },
        {
            value: tempStaticData?.maxValue,
            label: `${tempStaticData?.maxValue}°F`,
        },
    ];

    const dispatch = useDispatch();
    const getHeaterConfigInfo = async () => {
        try {
            dispatch(showLoader());
            const response = await client.query({
                query: GET_MACHINE_CONFIG,
                fetchPolicy: 'network-only',
            });
            const heaterDetails = response?.data?.getMachineConfig;
            setHeaterConfigInfo(heaterDetails);
            dispatch(hideLoader());
            console.warn('response in get heater config info:: ', response);
        } catch (error) {
            dispatch(hideLoader());
            console.error('error in get heater config info:: ', error);
        }
    };
    useEffect(() => {
        getHeaterConfigInfo();
        setErrorHeaterName(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        setIsHeaterNameConfigPending(heaterDetails?.pending_config?.device_name?.value || false);
        setIsHeaterTemperatureConfigPending(heaterDetails?.pending_config?.temp_degf?.value || false);
        setHeaterName(
            heaterDetails?.active_config?.device_name?.value || heaterDetails?.device_name || 'No Data Available',
        );
        setHeaterTemperature(heaterDetails?.active_config?.temp_degf?.value || heaterDetails?.temperature);
        setHeaterNameUpdatedAt(heaterDetails?.active_config?.device_name?.createdAt);
        setHeaterTemperatureUpdatedAt(heaterDetails?.active_config?.temp_degf?.createdAt);
    }, [heaterDetails]);

    const handleHeaterName = (e: any) => {
        let { value } = e.target;
        value = value.replace(/^\s+/, ''); // Allow only alphanumeric characters and hyphens
        setHeaterName(value);
        setErrorHeaterName(false);
    };

    const handleDashboardData = useCallback((incomingData: Partial<MachineConfigData>) => {
        console.warn(incomingData, 'incomingData from socket');

        const { device_name, device_name_timestamp, desired_temperature, temp_timestamp } = incomingData ?? {};
        if (device_name) {
            setHeaterName(device_name);
            setIsHeaterNameConfigPending(false);
        }
        if (device_name_timestamp) {
            setHeaterNameUpdatedAt(device_name_timestamp);
            setIsHeaterNameConfigPending(false);
        }
        if (desired_temperature) {
            setHeaterTemperature(desired_temperature);
            setIsHeaterTemperatureConfigPending(false);
        }
        if (temp_timestamp) {
            setHeaterTemperatureUpdatedAt(temp_timestamp);
            setIsHeaterTemperatureConfigPending(false);
        }
    }, []); // Add proper dependencies

    useSocket(handleDashboardData);

    useEffect(() => {
        if (token) {
            WSInstance.disconnect();
            WSInstance.connect(token);
            WSInstance?.on(`device/${userId}/config`, data => {
                handleDashboardData(data);
            });
        }
    }, [handleDashboardData, userId, token]);

    const callSaveHeaterNameAPI = async () => {
        try {
            if (!heaterName) {
                setShowEditButton(false);
                setErrorHeaterName(true);
                return;
            }
            dispatch(showLoader());
            const input: any = {
                device_name: heaterName,
            };

            const response = await client.mutate({
                mutation: UPDATE_MACHINE_CONFIG,
                variables: {
                    input,
                },
            });
            setShowEditButton(true);
            if (response?.data?.updateMachineConfig?.success) {
                dispatch(hideLoader());
                dispatch(
                    openToast({
                        show: true,
                        message:
                            response?.data?.updateMachineConfig?.message || t('machine_config_updated_success_message'),
                        severity: 'success',
                        isAlertOpen: true,
                    }),
                );
                getHeaterConfigInfo();
            }
            console.warn('response in update heater name config:: ', response);
        } catch (error) {
            dispatch(hideLoader());
            console.error('error in update heater name config:: ', error);
        }
    };

    const removeDeviceHandler = async () => {
        if (!deviceId) return;
        try {
            dispatch(showLoader());
            const { data } = await client.mutate({
                mutation: RemoveDeviceMutation,
                variables: {
                    input: {
                        device_id: deviceId,
                    },
                },
            });
            if (data?.deleteMachine?.success) {
                dispatch(
                    openToast({
                        show: true,
                        message: data?.deleteMachine?.message || t('remove_heater_success_msg'),
                        severity: 'success',
                        isAlertOpen: true,
                    }),
                );
                dispatch(setIsMetricsAPICalled(false));
                dispatch(
                    setMetricsData({
                        power_sensor_timestamp: '',
                        water_sensor_timestamp: '',
                        avg_gpu_util_7d: '',
                        avg_gpu_util_30d: '',
                        power_usage: '',
                        water_temperature: '',
                        temperature_history: '',
                        power: '',
                        internet: '',
                        machine_id: '',
                        heatingStatus: false,
                        computingStatus: false,
                    }),
                );
                dispatch(hideLoader());
                navigate(NavigationConfig.Home);
            }
        } catch (err) {
            dispatch(hideLoader());
            console.error('Mutation error:', err);
        } finally {
            dispatch(hideLoader());
        }
    };

    const handleEditHeaterName = () => {
        setShowEditButton(!showEditButton);
        if (showEditButton && heaterName === 'No Data Available') {
            setHeaterName('');
        }
        if (!showEditButton) {
            callSaveHeaterNameAPI();
        }
    };

    const handleSetWaterTemperature = (e: any) => {
        setHeaterTemperature(e.target.value);
        setShowApplyButton(true);
    };
    const callSaveHeaterTemperatureAPI = async () => {
        try {
            dispatch(showLoader());
            const input: any = {
                temp_degf: heaterTemperature,
            };

            const response = await client.mutate({
                mutation: UPDATE_MACHINE_CONFIG,
                variables: {
                    input,
                },
            });
            setShowApplyButton(false);
            if (response?.data?.updateMachineConfig?.success) {
                dispatch(hideLoader());
                dispatch(
                    openToast({
                        show: true,
                        message: t('machine_config_updated_success_message'),
                        severity: 'success',
                        isAlertOpen: true,
                    }),
                );
                getHeaterConfigInfo();
            }
            console.warn('response in update heater temperature config:: ', response);
        } catch (error) {
            dispatch(hideLoader());
            console.error('error in update heater temperature config:: ', error);
        }
    };

    const onPopupCloseHandler = async () => {
        setShowRemoveHeaterPopup(false);
    };
    const onShowRemoveHeaterAlertHandler = async () => {
        setShowRemoveHeaterPopup(true);
    };

    const onHeaterNameDiscardHandler = () => {
        setShowEditButton(!showEditButton);
        setHeaterName(
            heaterDetails?.active_config?.device_name?.value || heaterDetails?.device_name || 'No Data Available',
        );
        setErrorHeaterName(false);
    };

    const onTempDiscardHandler = () => {
        setShowApplyButton(false);
        setHeaterTemperature(heaterDetails?.active_config?.temp_degf?.value || heaterDetails?.temperature);
    };

    const renderValueLabel = (value: number, marks: any) => (
        <CustomValueLabel value={value} marks={marks} isValue={!!heaterTemperature} />
    );
    return (
        <HeaterDetailWrapper height="auto !Important">
            <Grid2 container width="100%" className="contactUs">
                <Grid2 size={{ xs: 12, lg: 6 }} p={{ xs: 1, lg: 5 }}>
                    <Box className="cstTextFeild">
                        <Typography pb={1.5} variant="h5">
                            {t('watter_name')}
                        </Typography>
                        <Box>
                            <TextFieldComponent
                                variant="outlined"
                                name="heaterName"
                                type="text"
                                placeholder="Enter WATTER Name"
                                value={heaterName}
                                onChange={handleHeaterName}
                                disabled={showEditButton}
                                className="cstHeaterName"
                            />
                            {showEditButton && (
                                <ButtonComponent
                                    className="cstEdit cstModifyBtn"
                                    buttonName="Edit"
                                    onClick={handleEditHeaterName}
                                />
                            )}
                            {errorHeaterName && (
                                <Typography variant="subtitle1" color="error">
                                    {t('watter_name_can_not_empty')}
                                </Typography>
                            )}
                        </Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box pt={1}>
                                {heaterNameUpdatedAt && (
                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            color={isHeaterNameConfigPending ? colors.errorTextBtn : 'info.main'}
                                        >
                                            {t('last_updated_at')}:
                                        </Typography>
                                        <Typography
                                            variant="subtitle2"
                                            color={isHeaterNameConfigPending ? colors.errorTextBtn : 'info.main'}
                                            fontFamily="good_sansbold"
                                        >
                                            {formatDateWithTimeWithSeconds(heaterNameUpdatedAt)}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                            <Box display="flex" gap={1.5} alignItems="center">
                                {isHeaterNameConfigPending && (
                                    <Typography
                                        display="flex"
                                        color={colors.errorTextBtn}
                                        alignItems="center"
                                        gap={1}
                                        variant="subtitle2"
                                    >
                                        {t('unsynced_changes')} <SyncDisabledIcon />
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                        {!showEditButton && (
                            <Box display="flex" gap={2} alignItems="center" mt={2}>
                                <ButtonComponent
                                    className="  cstCancelBtn"
                                    buttonName="Cancel"
                                    onClick={onHeaterNameDiscardHandler}
                                />
                                <ButtonComponent
                                    className=" cstModifyBtn"
                                    buttonName="Save"
                                    onClick={handleEditHeaterName}
                                />
                            </Box>
                        )}
                    </Box>
                    <Box pt={3} className="cstSlider">
                        <Box display="flex" gap={2} alignItems="center">
                            <Typography pb={1.5} variant="h5" width="90%">
                                {t('set_water_temperature')}
                            </Typography>
                        </Box>
                        <Box sx={{ width: '100%', overflow: 'visible', marginTop: '15px', px: '10px' }}>
                            <Slider
                                aria-label="Always visible"
                                step={1}
                                name="heaterTemperature"
                                marks={marks}
                                min={marks[0].value}
                                max={marks[1].value}
                                valueLabelDisplay="on"
                                onChange={handleSetWaterTemperature}
                                value={heaterTemperature}
                                // eslint-disable-next-line react/no-unstable-nested-components
                                valueLabelFormat={value => renderValueLabel(value, marks)}
                            />
                        </Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box pt={1}>
                                {heaterTemperatureUpdatedAt && (
                                    <>
                                        <Typography
                                            variant="subtitle2"
                                            color={isHeaterTemperatureConfigPending ? colors.errorTextBtn : 'info.main'}
                                        >
                                            {t('last_updated_at')}:
                                        </Typography>
                                        <Typography
                                            variant="subtitle2"
                                            color={isHeaterTemperatureConfigPending ? colors.errorTextBtn : 'info.main'}
                                            fontFamily="good_sansbold"
                                        >
                                            {formatDateWithTimeWithSeconds(heaterTemperatureUpdatedAt)}
                                        </Typography>
                                    </>
                                )}
                            </Box>
                            <Box display="flex" gap={1.5} alignItems="center">
                                {isHeaterTemperatureConfigPending && (
                                    <Typography
                                        display="flex"
                                        color={colors.errorTextBtn}
                                        alignItems="center"
                                        gap={1}
                                        variant="subtitle2"
                                    >
                                        {t('unsynced_changes')} <SyncDisabledIcon />
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                        {showApplyButton && (
                            <Box display="flex" gap={2} alignItems="center" mt={2}>
                                <ButtonComponent
                                    className="cstEdit  cstCancelBtn"
                                    buttonName="Cancel"
                                    onClick={onTempDiscardHandler}
                                />
                                <ButtonComponent
                                    className="cstEdit cstModifyBtn"
                                    buttonName="Apply"
                                    onClick={callSaveHeaterTemperatureAPI}
                                />
                            </Box>
                        )}
                    </Box>
                    <ButtonComponent
                        sx={{
                            background: '#BF3636',
                            color: 'primary.main',
                            padding: '17px 29px',
                            width: '99%',
                            marginTop: '25px',
                        }}
                        buttonName="Remove heater"
                        onClick={onShowRemoveHeaterAlertHandler}
                    />
                </Grid2>
                <Grid2 size={{ xs: 12, lg: 6 }}>
                    <img
                        src={watterImage}
                        alt="Heater Details"
                        width="100%"
                        style={{ position: 'relative', bottom: -5 }}
                    />
                </Grid2>
            </Grid2>
            <RemoveHeaterPopup
                acceptHandler={removeDeviceHandler}
                rejectHandler={onPopupCloseHandler}
                open={isShowRemoveHeaterPopup}
            />
        </HeaterDetailWrapper>
    );
};

export default HeaterDetails;
